import React, { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { Button, Divider, Input } from 'antd';

import { useSector } from '../hooks';
import Select from '@/components/ui/Select';
import FormItem from '@/components/ui/FormItem';
import { useCreateSector } from '@/hooks/v1/useSectorMutations';
import { useApplicationContext } from '@/context/v1/Application/context';

interface SectorFormItemProps {
	fieldName?: string | string[];
	required?: boolean;
}

export function SectorFormItem({ fieldName, required = true }: Readonly<SectorFormItemProps>): JSX.Element {
	const [sectorName, setSectorName] = useState<string>('');

	const { organization, company } = useApplicationContext();
	const { isLoading: isCreateSectorLoading, mutateAsync: createSector } = useCreateSector();

	const { isFetching, items, sectorId, isDisabled, handleClearSector, handleSectorSelect } = useSector({
		fieldName
	});

	async function handleCreateSector(): Promise<void> {
		const name = sectorName.trim();

		if (!name) return;

		const payload = {
			organization_id: organization.id!,
			company_id: company.id!,
			company_id_creation: company.id!,
			name
		};

		const result = await createSector({ payload });

		const newId = result?.data?.id;

		if (newId) {
			handleSectorSelect(newId);
		}

		setSectorName('');
	}

	return (
		<FormItem label="Sector" name={fieldName} required={required} requiredMessage="The field requires an answer">
			<Select
				allowClear
				showSearch
				options={items}
				value={sectorId}
				loading={isFetching}
				disabled={isDisabled}
				onClear={handleClearSector}
				onChange={handleSectorSelect}
				placeholder="Sector"
				dropdownRender={(menu) => (
					<>
						{menu}
						<Divider style={{ margin: '8px 0' }} />
						<div style={{ padding: '0 8px 4px', display: 'flex', gap: 8 }}>
							<Input
								value={sectorName}
								onChange={(e) => setSectorName(e.target.value)}
								placeholder={I18n.get('New sector name')}
								style={{ flex: 1 }}
							/>
							<Button
								type="text"
								onClick={handleCreateSector}
								loading={isCreateSectorLoading}
								disabled={!sectorName.trim() || sectorName.trim().length < 2}
							>
								{I18n.get('Add')}
							</Button>
						</div>
					</>
				)}
			/>
		</FormItem>
	);
}
